class GaugeWidget extends BaseWidget<GaugeResultVM, GaugeConfig> {
    override widgetType = WidgetType.Gauge;

    constructor(container: any, tab: WorkbookTab, $reportContent, dashboardWidget: DashboardWidget) {
        super(container, tab, $reportContent, dashboardWidget);
        this.disableDrilldown = true;
    }

    setConfig(config) {
        this.itemConfig = new GaugeConfig(config);
    }

    setResult(result) {
        this.result = new GaugeResultVM(result);

        let self = this;
        let widgetBackgroundColor = ko.computed(function () {
            return self.dashboardWidget.appearance.backgroundColor() || "#fff";
        });

        let ranges = this.getRanges();
        this.updateEndValue();

        this.result.gaugeSettings = {
            width: "100%",
            height: "100%",
            animation: {
                enabled: !result ? false : !workbook.workbookConfig.isPrinting
            },
            value: this.value,
            subvalues: [this.targetValue],
            scale: {
                startValue: ranges[0].startValue,
                endValue: ranges[ranges.length - 1].endValue,
                tickInterval: this.tickInterval,
                minorTickInterval: this.tickInterval / 2,
                orientation: "inside",
                label: {
                    indentFromTick: this.indentFromTick,
                    customizeText: function (e) {
                        let accountingStyle = self.itemConfig.accountingStyle() === -1 ? self.result?.metric?.accountingStyle || false : self.itemConfig.accountingStyle() ? true : false;
                        let decimalPlaces = self.itemConfig.scalesFormat.decimalPlaces() === -1 ? self.result?.metric?.decimalPlaces || 0 : self.itemConfig.scalesFormat.decimalPlaces();
                        let format = self.itemConfig.format() === FieldFormatEnum.None ? self.result?.metric?.metricFormat || FieldFormatEnum.Numeric : self.itemConfig.format();
                        return self.formatValue(e.value, self.itemConfig.scalesFormat.unitFormat(), decimalPlaces, format, accountingStyle);
                    },
                    font: {
                        color: self.itemConfig.scalesFormat.labelColor as any,
                        size: self.itemConfig.scalesFormat.labelFontSize as any
                    }
                },
                tick: {
                    visible: self.itemConfig.tick(),
                    width: 2,
                    length: this.tickLength,
                    color: widgetBackgroundColor as any,
                },
                minorTick: {
                    visible: this.itemConfig.tick(),
                    width: 1,
                    length: this.minorTickLength,
                    color: widgetBackgroundColor as any,
                },
            },
            rangeContainer: {
                width: this.itemConfig.size(),
                offset: 10,
                orientation: "inside",
                ranges: ranges,
            },

            valueIndicator: {
                type: this.itemConfig.type() === GaugeTypeEnum.Circular ? "twoColorNeedle" : "rangeBar",
                color: this.getIndicatorColor(true),
                secondColor: '#808080',
                size: this.itemConfig.size(),
                offset: this.indicatorOffset,
                width: this.valueIndicatorWidth,
                text: {
                    customizeText: function (e) {
                        let accountingStyle = self.itemConfig.accountingStyle() === -1 ? self.result?.metric?.accountingStyle || false : self.itemConfig.accountingStyle() ? true : false;
                        let decimalPlaces = self.itemConfig.indicatorFormat.decimalPlaces() === -1 ? self.result?.metric?.decimalPlaces || 0 : self.itemConfig.indicatorFormat.decimalPlaces();
                        let format = self.itemConfig.format() === FieldFormatEnum.None ? self.result?.metric?.metricFormat || FieldFormatEnum.Numeric : self.itemConfig.format();
                        return self.formatValue(e.value, self.itemConfig.indicatorFormat.unitFormat(), decimalPlaces, format, accountingStyle);
                    }
                }
            },
            subvalueIndicator: {
                type: "triangleMarker",
                palette: ['#808080'],
                width: 15,
                offset: this.subvalueIndicatorOffset,
            },
            centerTemplate: (gauge, container) => {
                if (self.itemConfig.showIndicatorValue() === GaugeShowIndicatorValueEnum.Middle) {
                    const rect = self.createRect(50, 50, 'transparent');
                    let value = "0";
                    if (result) {
                        let accountingStyle = self.itemConfig.accountingStyle() === -1 ? self.result?.metric?.accountingStyle || false : self.itemConfig.accountingStyle() ? true : false;
                        let decimalPlaces = self.itemConfig.indicatorFormat.decimalPlaces() === -1 ? self.result?.metric?.decimalPlaces || 0 : self.itemConfig.indicatorFormat.decimalPlaces();
                        let format = self.itemConfig.format() === FieldFormatEnum.None ? self.result?.metric?.metricFormat || FieldFormatEnum.Numeric : self.itemConfig.format();
                        value = self.formatValue(gauge.value() !== undefined ? gauge.value() : 0, self.itemConfig.indicatorFormat.unitFormat(), decimalPlaces, format, accountingStyle);
                    }
                    //Apply a proportion to the width or height of the smaller size
                    let minSize = self.$reportContent.width() < self.$reportContent.height() ? self.$reportContent.width() : self.$reportContent.height();
                    let fontSize = self.itemConfig.fontSize() / 100 * (minSize);
                    const text = self.createText(20, minSize / 2, fontSize, 'middle', value, self.getIndicatorColor(false));

                    container.appendChild(rect);
                    container.appendChild(text);
                }
            },
            margin: {
                top: 10,
                bottom: 10,
                left: 10,
                right: 10
            },
            tooltip: {
                enabled: true,
                zIndex: 99999,
                customizeTooltip(arg: any) {
                    if (arg.type === "subvalue-indicator") {
                        let value = self.formatValue(arg.value, LabelFormatEnum.Normal, self.result.target.decimalPlaces, self.result.target.metricFormat, self.result.target.accountingStyle);
                        return {
                            text: `Target: ${value}`
                        }
                    }

                    let value = self.formatValue(arg.value, LabelFormatEnum.Normal, self.result.metric.decimalPlaces, self.result.metric.metricFormat, self.result.metric.accountingStyle);
                    return {
                        //TODO
                        //text: `${vm.metricName()}: ${value}`
                        text: value
                    }
                },
            },
            onInitialized(e) {
                self.result.gaugeComponent = e.component;
            }
        }
    }

    setupSettings() {
        let self = this;
        let update = function () {
            self.itemConfig.endValue(null);
            self.updateEndValue();

            self.result.gaugeComponent.beginUpdate();
            let ranges = self.getRanges();
            self.result.gaugeComponent.option("value", self.value);
            self.result.gaugeComponent.option("subvalues", [self.targetValue]);
            self.result.gaugeComponent.option("scale.startValue", ranges[0].startValue);
            self.result.gaugeComponent.option("scale.endValue", ranges[ranges.length - 1].endValue);
            self.result.gaugeComponent.option("rangeContainer.ranges", ranges);
            self.result.gaugeComponent.option("valueIndicator.color", self.getIndicatorColor(true));
            self.result.gaugeComponent.endUpdate();
        };

        this.createSettingItem(this.itemConfig.formattingRule, "formattingRule", UpdateSettingMode.Custom, update);
        //this.createSettingItem(this.itemConfig.metric, "metric", UpdateSettingMode.Reload, update);
        //this.createSettingItem(this.itemConfig.target, "target", UpdateSettingMode.Reload, update);

        this.createSettingItem(this.itemConfig.type, "type", UpdateSettingMode.Custom, () => {
            self.result.gaugeComponent.beginUpdate();
            self.result.gaugeComponent.option("valueIndicator.type", self.itemConfig.type() === GaugeTypeEnum.Circular ? "twoColorNeedle" : "rangeBar");
            self.result.gaugeComponent.option("valueIndicator.offset", self.indicatorOffset);
            self.result.gaugeComponent.option("rangeContainer.ranges", self.getRanges());
            self.result.gaugeComponent.option("valueIndicator.color", self.getIndicatorColor(true));
            self.result.gaugeComponent.option("valueIndicator.width", self.valueIndicatorWidth);
            self.result.gaugeComponent.option("subvalueIndicator.offset", self.subvalueIndicatorOffset);
            self.result.gaugeComponent.endUpdate();
        });

        this.createSettingItem(this.itemConfig.showIndicatorValue, "showIndicatorValue", UpdateSettingMode.Custom, () => {
            self.result.gaugeComponent.beginUpdate();
            //HACK to redraw the indicator text
            self.result.gaugeComponent.option("valueIndicator.size", 0);
            self.result.gaugeComponent.option("valueIndicator.size", self.itemConfig.size());
            //***
            self.result.gaugeComponent.endUpdate();
        });

        this.createSettingItem(this.itemConfig.size, "size", UpdateSettingMode.Custom, () => {
            self.result.gaugeComponent.beginUpdate();
            self.result.gaugeComponent.option("valueIndicator.size", self.itemConfig.size());
            self.result.gaugeComponent.option("rangeContainer.width", self.itemConfig.size());
            self.result.gaugeComponent.option("scale.tick.length", self.tickLength);
            self.result.gaugeComponent.option("scale.minorTick.length", self.minorTickLength);
            self.result.gaugeComponent.option("scale.label.indentFromTick", self.indentFromTick);
            self.result.gaugeComponent.endUpdate();
            self.result.gaugeComponent.render();
        });

        let updateTicks = function () {
            self.result.gaugeComponent.beginUpdate();
            self.result.gaugeComponent.option("scale.tickInterval", self.tickInterval);
            self.result.gaugeComponent.option("scale.minorTickInterval", self.tickInterval / 2);
            self.result.gaugeComponent.option("scale.tick.visible", self.itemConfig.tick());
            self.result.gaugeComponent.option("scale.minorTick.visible", self.itemConfig.tick());
            self.result.gaugeComponent.option("scale.label.indentFromTick", self.indentFromTick);
            self.result.gaugeComponent.endUpdate();
        };
        this.createSettingItem(this.itemConfig.tick, "tick", UpdateSettingMode.Custom, updateTicks);
        this.createSettingItem(this.itemConfig.tickIntervalType, "tickIntervalType", UpdateSettingMode.Custom, updateTicks);
        this.createSettingItem(this.itemConfig.tickIntervalValue, "tickIntervalValue", UpdateSettingMode.Custom, updateTicks);

        let updateFormatValue = function () {
            self.result.gaugeComponent.beginUpdate();
            //HACK to redraw the indicator text
            self.result.gaugeComponent.option("valueIndicator.size", 0);
            self.result.gaugeComponent.option("valueIndicator.size", self.itemConfig.size());
            //***

            self.result.gaugeComponent.option("value", self.value);
            self.result.gaugeComponent.option("subvalues", [self.targetValue]);

            self.result.gaugeComponent.endUpdate();
        };
        this.createSettingItem(this.itemConfig.indicatorFormat.decimalPlaces, "indicatorFormat.decimalPlaces", UpdateSettingMode.Custom, updateFormatValue);
        this.createSettingItem(this.itemConfig.indicatorFormat.unitFormat, "indicatorFormat.unitFormat", UpdateSettingMode.Custom, updateFormatValue);
        this.createSettingItem(this.itemConfig.scalesFormat.decimalPlaces, "scalesFormat.decimalPlaces", UpdateSettingMode.Custom, updateFormatValue);
        this.createSettingItem(this.itemConfig.scalesFormat.unitFormat, "scalesFormat.unitFormat", UpdateSettingMode.Custom, updateFormatValue);
        this.createSettingItem(this.itemConfig.accountingStyle, "accountingStyle", UpdateSettingMode.Custom, updateFormatValue);
        this.createSettingItem(this.itemConfig.format, "format", UpdateSettingMode.Custom, updateFormatValue);

        this.createSettingItem(this.itemConfig.scalesFormat.position, "scalesFormat.position", UpdateSettingMode.Custom, () => {
            self.result.gaugeComponent.beginUpdate();
            self.result.gaugeComponent.option("scale.label.indentFromTick", self.indentFromTick);
            self.result.gaugeComponent.option("scale.tick.length", self.tickLength);
            self.result.gaugeComponent.option("scale.minorTick.length", self.minorTickLength);
            self.result.gaugeComponent.endUpdate();
        });
        this.createSettingItem(this.itemConfig.scalesFormat.labelFontSize, "scalesFormat.labelFontSize", UpdateSettingMode.Automatic);
        this.createSettingItem(this.itemConfig.scalesFormat.labelColor, "scalesFormat.labelColor", UpdateSettingMode.Automatic);

        this.createSettingItem(this.itemConfig.endValue, "endValue", UpdateSettingMode.Custom, () => {
            if (self.result?.gaugeComponent) {
                self.result.gaugeComponent.beginUpdate();
                let ranges = self.getRanges();
                self.result.gaugeComponent.option("rangeContainer.ranges", ranges);
                self.result.gaugeComponent.option("scale.startValue", ranges[0].startValue);
                self.result.gaugeComponent.option("scale.endValue", ranges[ranges.length - 1].endValue);
                self.result.gaugeComponent.option("scale.tickInterval", self.tickInterval);
                self.result.gaugeComponent.option("scale.minorTickInterval", self.tickInterval / 2);
                self.result.gaugeComponent.endUpdate();
            }
        });

        this.createSettingItem(this.itemConfig.fontSize, "fontSize", UpdateSettingMode.Custom, () => {
            self.result.gaugeComponent.beginUpdate();
            //HACK to redraw the indicator text
            self.result.gaugeComponent.option("valueIndicator.size", 0);
            self.result.gaugeComponent.option("valueIndicator.size", self.itemConfig.size());
            //***
            self.result.gaugeComponent.endUpdate();
        });

        this.createSettingItem(this.itemConfig.comparisonFilter.dateUnitValue, "comparisonFilter.dateUnitValue", UpdateSettingMode.Reload);
        this.createSettingItem(this.itemConfig.comparisonFilter.dateOption, "comparisonFilter.dateOption", UpdateSettingMode.Reload);
        this.createSettingItem(this.itemConfig.comparisonFilter.dateUnit, "comparisonFilter.dateUnit", UpdateSettingMode.Reload);
        this.createSettingItem(this.itemConfig.comparisonFilter.dateRange, "comparisonFilter.dateRange", UpdateSettingMode.Reload);
    }

    repaint() {

    }

    runSetup() {

    }

    setup() {


    }
    private get tickInterval() {
        switch (this.itemConfig.tickIntervalType()) {
            case GaugeTickIntervalTypeEnum.Value:
                return this.itemConfig.tickIntervalValue();
            case GaugeTickIntervalTypeEnum.Percentual:
                let ranges = this.getRanges();
                let startValue = ranges[0].startValue;
                let endValue = ranges[ranges.length - 1].endValue;
                return Math.abs((endValue - startValue) * this.itemConfig.tickIntervalValue() / 100);
            default:
                break;
        }
    }
    private get indentFromTick() {
        return this.itemConfig.scalesFormat.position() === GaugeScalePositionEnum.Inside ? this.itemConfig.tick() ? -5 : -this.itemConfig.size() : 5;
    }
    private get tickLength() {
        return this.itemConfig.scalesFormat.position() === GaugeScalePositionEnum.Outside ? this.itemConfig.size() : this.itemConfig.size() /*+ 5*/;
    };
    private get minorTickLength() {
        return this.itemConfig.scalesFormat.position() === GaugeScalePositionEnum.Outside ? this.itemConfig.size() : this.itemConfig.size() /*+ 3*/;
    }
    private get indicatorOffset() {
        return this.itemConfig.type() === GaugeTypeEnum.Bar ? 10 : 20;
    }
    private get valueIndicatorWidth() {
        return 5;
    }
    private get subvalueIndicatorOffset() {
        return 15;
    }
    private get value() {
        if (this.result && this.result.value !== undefined && this.itemConfig.metric()) {
            if ((this.result.metric.metricFormat === FieldFormatEnum.Percentage && this.itemConfig.format() === FieldFormatEnum.None) || this.itemConfig.format() === FieldFormatEnum.Percentage) {
                return this.result.value * 100;
            }

            return this.result.value;
        }
        return 0;
    }
    private get targetValue() {
        if (this.result && this.result.targetValue !== undefined && this.itemConfig.target()) {
            if ((this.result.target.metricFormat === FieldFormatEnum.Percentage && this.itemConfig.format() === FieldFormatEnum.None) || this.itemConfig.format() === FieldFormatEnum.Percentage) {
                return this.result.targetValue * 100;
            }

            return this.result.targetValue;
        }
        return null;
    }

    private createRect(width, height, fill) {
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');

        rect.setAttribute('x', 0 as any);
        rect.setAttribute('y', 0 as any);
        rect.setAttribute('width', width);
        rect.setAttribute('height', height);
        rect.setAttribute('fill', fill);

        return rect;
    }
    private createText(x, y, fontSize, textAnchor, content, color) {
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');

        text.setAttribute('x', x);
        text.setAttribute('y', y);
        text.setAttribute('fill', color);
        text.setAttribute('text-anchor', textAnchor);
        text.setAttribute('font-size', fontSize);

        text.textContent = content;

        return text;
    }

    private formatValue(value: number, unitFormat: LabelFormatEnum, decimalPlaces: number, format: FieldFormatEnum, accountingStyle: boolean): string {
        let dx = DevExpress as any;
        let largerNumber = labelFormatEnumToString(unitFormat);
        const currencySign = accountingStyle ? "accounting" : "standard";
        const formatValue = format === FieldFormatEnum.None ? FieldFormatEnum.Numeric : format;

        if (formatValue === FieldFormatEnum.Currency)
            return `${dx.formatHelper.format(value, { style: "currency", currency: this.result?.currencyCode || "USD", currencyDisplay: "narrowSymbol", precision: decimalPlaces, currencySign: currencySign, largerNumber: largerNumber })}`;
        else if (formatValue === FieldFormatEnum.Percentage)
            return `${dx.formatHelper.format(value / 100, { style: "percent", precision: decimalPlaces, currencySign: currencySign })}`;

        return `${dx.formatHelper.format(value, { style: "decimal", precision: decimalPlaces, largerNumber: largerNumber, currencySign: currencySign })}`;
    }

    private getRanges() {
        let ranges = [];
        if (this.itemConfig.formattingRule() && this.itemConfig.metric()) {
            if (this.itemConfig.type() === GaugeTypeEnum.Circular) {
                let formattingRuleObj = FormattingRuleService.getFormattingRuleById(this.itemConfig.formattingRule())
                if (formattingRuleObj) {
                    let frValues = formattingRuleObj.scales.map((x) => ({ minValue: x.minValue, backgroundColor: x.color || x.backgroundColor }));
                    if (frValues.length > 0) {
                        for (var i = 0; i < frValues.length - 1; i++) {
                            let scale = frValues[i] as any;
                            let nextScale = frValues[i + 1] as any;
                            ranges.push({ startValue: scale.minValue, endValue: nextScale.minValue, color: scale.backgroundColor });
                        }

                        let lastScale = frValues[frValues.length - 1] as any;
                        ranges.push({ startValue: lastScale.minValue, endValue: this.itemConfig.endValue() === null ? lastScale.minValue : this.itemConfig.endValue(), color: lastScale.backgroundColor });
                    }
                }
            }
        }

        if (ranges.length == 0) {
            ranges.push({ startValue: 0, endValue: this.itemConfig.endValue() === null ? 100 : this.itemConfig.endValue(), color: '#EAEAEA' });
        }

        if (ranges.length > 0 && this.result) {
            let value = this.value;
            let targetValue = this.targetValue;

            var setRange = function (value) {
                if (value !== null && value !== undefined) {
                    if (ranges[0].startValue > value) {
                        ranges[0].startValue = value;
                    }

                    if (ranges[ranges.length - 1].endValue < value) {
                        ranges[ranges.length - 1].endValue = value;
                    }
                }
            }

            setRange(value);
            setRange(targetValue);
        }

        return ranges;
    }

    private getIndicatorColor(isPrincipalIndicator: boolean = false) {
        let defaultColor = isPrincipalIndicator ? "#A9A9A9" : '#000';
        if (this.itemConfig.type() === GaugeTypeEnum.Circular && isPrincipalIndicator) {
            return defaultColor;
        }
        else {
            if (this.itemConfig.formattingRule() && this.itemConfig.metric()) {
                let formattingRuleObj = FormattingRuleService.getFormattingRuleById(this.itemConfig.formattingRule())
                if (formattingRuleObj) {
                    let value = this.value;
                    let formattingScale = FormattingRuleService.getFormattingRuleScale(formattingRuleObj, value, false);
                    if (formattingScale) {
                        return formattingScale.color || formattingScale.backgroundColor;
                    }
                }
            }

            return defaultColor;
        }
    }

    private updateEndValue() {
        if (this.result && this.result.value !== undefined && this.itemConfig.formattingRule() && this.itemConfig.metric()) {
            if (this.itemConfig.endValue() === null) {
                let formattingRuleObj = FormattingRuleService.getFormattingRuleById(this.itemConfig.formattingRule())
                if (formattingRuleObj) {
                    let frValues = formattingRuleObj.scales.map((x) => ({ minValue: x.minValue }));
                    if (frValues.length > 0) {
                        this.itemConfig.endValue(frValues[frValues.length - 1].minValue * 1.3);
                    }
                    else {
                        this.itemConfig.endValue(0);
                    }
                }
            }
        }
    }
}